<script lang="ts" generics="T extends string | number">
  /** WAI-ARIA の tablist。矢印・Home・End で移動し、移動した時点で選ぶ（自動選択） */
  interface Props {
    label: string;
    items: { id: T; label: string; n?: number }[];
    selected: T | undefined;
    /** パネルの id（aria-controls） */
    panelId: string;
    idPrefix: string;
    onselect: (id: T) => void;
  }

  let { label, items, selected, panelId, idPrefix, onselect }: Props = $props();
  let list: HTMLDivElement | undefined = $state();

  function onkeydown(e: KeyboardEvent) {
    const i = items.findIndex((t) => t.id === selected);
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next < 0) return;
    e.preventDefault();
    e.stopPropagation();
    const item = items[next];
    if (!item) return;
    onselect(item.id);
    list?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  }
</script>

<div class="tabs" role="tablist" aria-label={label} bind:this={list} tabindex="-1" {onkeydown}>
  {#each items as item, i (item.id)}
    <button
      type="button"
      role="tab"
      id="{idPrefix}-{item.id}"
      aria-selected={item.id === selected}
      aria-controls={panelId}
      tabindex={item.id === selected || (selected === undefined && i === 0) ? 0 : -1}
      onclick={() => onselect(item.id)}
    >
      {#if item.n !== undefined}<span class="n" aria-hidden="true">{item.n}</span>{/if}{item.label}
    </button>
  {/each}
</div>

<style>
  .tabs {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--border);
    overflow-x: auto;
  }
  [role='tab'] {
    display: inline-flex;
    align-items: center;
    min-height: var(--tap-min);
    padding: 0 var(--space-4);
    border: 0;
    border-bottom: 3px solid transparent;
    background: none;
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    white-space: nowrap;
  }
  [role='tab']:hover {
    color: var(--text);
  }
  [role='tab'][aria-selected='true'] {
    color: var(--text);
    border-bottom-color: var(--accent);
  }
  [role='tab']:focus-visible {
    outline-offset: -2px;
  }
  .n {
    display: inline-grid;
    place-items: center;
    width: 1.4rem;
    height: 1.4rem;
    margin-right: var(--space-1);
    border-radius: 50%;
    border: 1px solid currentColor;
    font-size: var(--fs-xs);
  }
  @media (max-width: 639px) {
    [role='tab'] {
      padding: 0 var(--space-2);
      font-size: var(--fs-sm);
    }
  }
</style>
